import { MouseEvent, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { CaptionSM, HeadingMD, HeadingSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHistoryContent } from './OurHistoryContent'

const PROGRAMMATIC_SCROLL_DELAY_MS = 500

export const OurHistorySection = () => {
  const { t } = useTranslate('about')
  const history = useHistoryContent()
  const [activeSlide, setActiveSlide] = useState<number>(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const isScrollingProgrammatically = useRef(false)

  const canScrollBack = activeSlide > 0 && history.length > 0
  const canScrollForward = history.length > 0 && activeSlide < history.length - 1

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingProgrammatically.current) return

        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = slideRefs.current.indexOf(entry.target as HTMLDivElement)
            if (index !== -1) {
              setActiveSlide(index)
            }
          }
        }
      },
      { root: container, threshold: 0.5 },
    )

    for (const ref of slideRefs.current) {
      if (ref) observer.observe(ref)
    }

    return () => observer.disconnect()
  }, [history.length])

  const scrollToSlide = (index: number) => {
    setActiveSlide(index)
    const destination = slideRefs.current[index]
    if (destination) {
      isScrollingProgrammatically.current = true
      destination.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      setTimeout(() => {
        isScrollingProgrammatically.current = false
      }, PROGRAMMATIC_SCROLL_DELAY_MS)
    }
  }

  const scrollBack = () => scrollToSlide(activeSlide - 1)
  const scrollForward = () => scrollToSlide(activeSlide + 1)

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const index = Number((event.target as HTMLElement).id)
    if (!isNaN(index)) {
      scrollToSlide(index)
    }
  }

  return (
    <section className="bg-core-gray-900 py-[131px] text-center" id="history">
      <HeadingSM className="photon-heading-xl mx-auto max-w-[240px] pb-8 md:max-w-none" weight="bold">
        {t('historyOfAngel', 'History of Angel')}
      </HeadingSM>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="flex w-full snap-x snap-mandatory gap-8 overflow-x-scroll overscroll-x-contain scrollbar-none"
        >
          {history.map((data, index) => {
            const slideActive = activeSlide === index
            return (
              <div
                className={classNames('min-h-[300px]', {
                  'ml-24': index === 0,
                  'mr-24': index === history.length - 1,
                })}
                id={`slide-${index}`}
                key={index}
                ref={(el) => {
                  slideRefs.current[index] = el
                }}
              >
                <div
                  className={classNames(
                    'snap-center relative mx-auto h-[213px] w-[300px] overflow-hidden rounded-2xl transition-transform duration-300 ease-in-out md:h-[330px] md:w-[650px] xl:basis-1/2',
                    slideActive ? 'scale-100' : 'scale-75 lg:scale-50',
                  )}
                  onClick={handleClick}
                >
                  <Image
                    alt={data.image.alt}
                    fill
                    id={`${index}`}
                    loading="lazy"
                    priority={false}
                    src={data.image.src}
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <FlexColumn
                  aria-colindex={index}
                  className={classNames(
                    'mx-auto gap-8 pt-8 transition-opacity duration-300 ease-in-out md:px-8 md:w-[693px] xl:mx-auto xl:w-fit xl:max-w-[1024px] xl:flex-row',
                    slideActive ? 'opacity-100' : 'pointer-events-none opacity-0',
                  )}
                >
                  <HeadingMD className="photon-display text-left xl:my-auto" weight="bold">
                    {data.year}
                  </HeadingMD>
                  <CaptionSM className="lg:photon-caption-md text-left">{data.body}</CaptionSM>
                </FlexColumn>
              </div>
            )
          })}
        </div>
        <CarouselNav
          active={canScrollBack}
          className="top-0 p-2"
          direction="back"
          onClick={scrollBack}
          orientation="horizontal"
        />

        <CarouselNav
          active={canScrollForward}
          className="top-0 p-2"
          direction="forward"
          onClick={scrollForward}
          orientation="horizontal"
        />
      </div>
    </section>
  )
}
